import * as React from 'react';

/**
 * Count-up for KPI values: eases from the previously shown value (0 on first
 * mount) to `value` over ~600ms and renders `format(current)`. Pair with
 * `tabular-nums` on the element so digits don't jitter mid-animation. Renders
 * the final value immediately under prefers-reduced-motion.
 */
export function AnimatedNumber({
  value,
  format = (n: number) => String(Math.round(n)),
  duration = 600,
}: {
  value: number;
  format?: (n: number) => string;
  duration?: number;
}) {
  const [display, setDisplay] = React.useState(0);
  const prev = React.useRef(0);

  React.useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !Number.isFinite(value) || value === prev.current) {
      prev.current = value;
      setDisplay(value);
      return;
    }
    const from = prev.current;
    const delta = value - from;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + delta * eased);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prev.current = value;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{format(display)}</>;
}
